import React, { useState } from 'react';
import { EveningDebriefData, DailyCheckpoint } from '../../../types/isometric.types';

interface EveningDebriefModalProps {
  isOpen: boolean;
  checkpoint: DailyCheckpoint | null;
  data: EveningDebriefData | null;
  timeRemaining: number; // seconds until auto-proceed
  onSubmitFeedback: (feedback: { rating: 1 | 2 | 3 | 4 | 5; feedback: string }) => void;
  onSkip: () => void;
  onClose: () => void;
}

export const EveningDebriefModal: React.FC<EveningDebriefModalProps> = ({
  isOpen,
  checkpoint: _checkpoint,
  data,
  timeRemaining,
  onSubmitFeedback,
  onSkip,
  onClose: _onClose,
}) => {
  const [rating, setRating] = useState<1 | 2 | 3 | 4 | 5>(4);
  const [feedback, setFeedback] = useState('');
  const [highlightedSuccess, setHighlightedSuccess] = useState('');
  const [flaggedIssue, setFlaggedIssue] = useState('');

  if (!isOpen || !data) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const completedCount = data.accomplishments.filter((a) => a.status === 'COMPLETED').length;
  const totalCount = data.accomplishments.length;
  const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleSubmit = () => {
    onSubmitFeedback({
      rating,
      feedback: [feedback, highlightedSuccess && `Success: ${highlightedSuccess}`, flaggedIssue && `Issue: ${flaggedIssue}`]
        .filter(Boolean)
        .join('\n'),
    });
  };

  const getMetricChangeColor = (change: number) => {
    if (change > 0) return 'text-green-400';
    if (change < 0) return 'text-red-400';
    return 'text-slate-400';
  };

  const getMetricChangeIcon = (change: number) => {
    if (change > 0) return '↑';
    if (change < 0) return '↓';
    return '→';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-xl shadow-2xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">🌙</span>
                Evening Debrief
              </h2>
              <p className="text-purple-100 mt-1">
                Review your day's progress and provide feedback
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-purple-200">Auto-save in</div>
              <div className="text-2xl font-mono font-bold text-white">
                {formatTime(timeRemaining)}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Day Overview */}
          <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Today's Completion</h3>
                <p className="text-slate-400 text-sm">
                  {completedCount} of {totalCount} tasks completed
                </p>
              </div>
              <div className="text-right">
                <div
                  className={`text-4xl font-bold ${
                    completionRate >= 80
                      ? 'text-green-400'
                      : completionRate >= 50
                        ? 'text-yellow-400'
                        : 'text-red-400'
                  }`}
                >
                  {completionRate}%
                </div>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  completionRate >= 80
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                    : completionRate >= 50
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-500'
                      : 'bg-gradient-to-r from-red-500 to-orange-500'
                }`}
                style={{ width: `${completionRate}%` }}
              />
            </div>
          </div>

          {/* Accomplishments */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span>✅</span> Accomplishments
            </h3>
            <div className="space-y-2">
              {data.accomplishments.map((item, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg border flex items-center gap-3 ${
                    item.status === 'COMPLETED'
                      ? 'bg-green-900/20 border-green-700/50'
                      : item.status === 'PARTIAL'
                        ? 'bg-yellow-900/20 border-yellow-700/50'
                        : 'bg-red-900/20 border-red-700/50'
                  }`}
                >
                  <span
                    className={`text-xl ${
                      item.status === 'COMPLETED'
                        ? 'text-green-400'
                        : item.status === 'PARTIAL'
                          ? 'text-yellow-400'
                          : 'text-red-400'
                    }`}
                  >
                    {item.status === 'COMPLETED' ? '✓' : item.status === 'PARTIAL' ? '◐' : '✗'}
                  </span>
                  <span
                    className={
                      item.status === 'COMPLETED'
                        ? 'text-green-200'
                        : item.status === 'PARTIAL'
                          ? 'text-yellow-200'
                          : 'text-red-200'
                    }
                  >
                    {item.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Metrics Changes */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span>📊</span> KPI Changes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.metricsChanges.map((metric, i) => (
                <div
                  key={i}
                  className="p-4 bg-slate-800 rounded-lg border border-slate-700"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 text-sm">{metric.name}</span>
                    <span className={`font-bold ${getMetricChangeColor(metric.change)}`}>
                      {getMetricChangeIcon(metric.change)} {Math.abs(metric.change).toFixed(1)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-white">
                      {metric.currentValue.toFixed(1)}
                    </span>
                    <span className="text-slate-500 text-sm">
                      from {metric.previousValue.toFixed(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Challenges */}
          {data.challenges.length > 0 && (
            <div className="mb-6 p-4 bg-orange-900/20 rounded-lg border border-orange-700/50">
              <h3 className="text-sm font-semibold text-orange-400 uppercase tracking-wide mb-2">
                Challenges Encountered
              </h3>
              <ul className="space-y-2">
                {data.challenges.map((challenge, i) => (
                  <li key={i} className="text-orange-200 flex items-start gap-2">
                    <span className="text-orange-500 mt-0.5">⚠️</span>
                    <span>{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tomorrow Preview */}
          <div className="mb-6 p-4 bg-blue-900/20 rounded-lg border border-blue-700/50">
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-2">
              Tomorrow's Preview
            </h3>
            <p className="text-blue-200">{data.tomorrowPreview}</p>
          </div>

          {/* Rating */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              How was today's performance?
            </h3>
            <div className="flex gap-2 justify-center">
              {([1, 2, 3, 4, 5] as const).map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`text-4xl transition-transform hover:scale-110 ${
                    star <= rating ? 'text-yellow-400' : 'text-slate-600'
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
            <p className="text-center text-slate-400 mt-2 text-sm">
              {rating === 1
                ? 'Poor - Major issues'
                : rating === 2
                  ? 'Below average'
                  : rating === 3
                    ? 'Average - Room for improvement'
                    : rating === 4
                      ? 'Good - Met expectations'
                      : 'Excellent - Exceeded expectations'}
            </p>
          </div>

          {/* Feedback */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-400 mb-2">
              Additional Feedback (Optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Any thoughts on how to improve tomorrow..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-green-400 mb-2">
                🌟 Highlight a Success
              </label>
              <input
                type="text"
                value={highlightedSuccess}
                onChange={(e) => setHighlightedSuccess(e.target.value)}
                placeholder="What went well?"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white placeholder-slate-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-red-400 mb-2">
                🚩 Flag an Issue
              </label>
              <input
                type="text"
                value={flaggedIssue}
                onChange={(e) => setFlaggedIssue(e.target.value)}
                placeholder="What needs attention?"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white placeholder-slate-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-slate-800/50 border-t border-slate-700 flex items-center justify-between">
          <button
            onClick={onSkip}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Skip & Save for Later
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
          >
            Submit Feedback ✓
          </button>
        </div>
      </div>
    </div>
  );
};
